import React from 'react';

/**
 * Custom brand illustration for the login page hero panel.
 * Concept: "Financial MRI" — a medical-style health monitor whose ECG heartbeat
 * morphs into a rising bar chart, with a rupee symbol at the diaphragm and
 * three concentric "health rings" filling. Pure SVG + CSS keyframes — no assets.
 * Palette: brand indigo #1F135A, green #0AC5A8, cream #F7F5FF.
 */
export const FinancialMRIIllustration = () => (
  <div className="relative w-full h-full flex items-center justify-center" data-testid="hero-illustration">
    <style>{`
      /* Static end-state fallback — used when the user prefers reduced motion. */
      .mri-heart { transform-origin: 250px 250px; }
      .mri-ring-1 { stroke-dashoffset: 170; }
      .mri-ring-2 { stroke-dashoffset: 110; }
      .mri-ring-3 { stroke-dashoffset: 60; }
      .mri-ecg   { stroke-dasharray: 1000; stroke-dashoffset: 0; }
      .mri-bar   { transform-origin: bottom; opacity: 1; }
      .mri-coin-1, .mri-coin-2, .mri-coin-3 { opacity: 0; }
      .mri-scan  { opacity: 0; }

      @media (prefers-reduced-motion: no-preference) {
        @keyframes mri-pulse { 0%,100%{transform:scale(1);opacity:.9} 50%{transform:scale(1.04);opacity:1} }
        @keyframes mri-ring-1 { 0%{stroke-dashoffset:565} 100%{stroke-dashoffset:170} }
        @keyframes mri-ring-2 { 0%{stroke-dashoffset:440} 100%{stroke-dashoffset:110} }
        @keyframes mri-ring-3 { 0%{stroke-dashoffset:314} 100%{stroke-dashoffset:60} }
        @keyframes mri-ecg { 0%,100%{stroke-dashoffset:1000} 45%{stroke-dashoffset:0} 55%{stroke-dashoffset:0} }
        @keyframes mri-bar { 0%{transform:scaleY(0);opacity:0} 100%{transform:scaleY(1);opacity:1} }
        @keyframes mri-coin { 0%{transform:translateY(-8px);opacity:0} 40%{opacity:1} 100%{transform:translateY(24px);opacity:0} }
        @keyframes mri-glow { 0%,100%{opacity:.15} 50%{opacity:.35} }
        @keyframes mri-scan { 0%{transform:translateY(-140px);opacity:0} 15%{opacity:.35} 85%{opacity:.35} 100%{transform:translateY(140px);opacity:0} }

        .mri-heart { animation: mri-pulse 1.8s ease-in-out infinite; }
        .mri-ring-1 { animation: mri-ring-1 2.4s ease-out forwards; }
        .mri-ring-2 { animation: mri-ring-2 2.4s .2s ease-out forwards; }
        .mri-ring-3 { animation: mri-ring-3 2.4s .4s ease-out forwards; }
        .mri-ecg   { animation: mri-ecg 4s ease-in-out infinite; }
        .mri-bar   { animation: mri-bar .8s cubic-bezier(.34,1.56,.64,1) forwards; opacity:0; }
        .mri-bar-1 { animation-delay: .8s } .mri-bar-2 { animation-delay: 1.0s }
        .mri-bar-3 { animation-delay: 1.2s } .mri-bar-4 { animation-delay: 1.4s }
        .mri-bar-5 { animation-delay: 1.6s }
        .mri-coin-1 { animation: mri-coin 3.2s ease-in-out infinite; }
        .mri-coin-2 { animation: mri-coin 3.2s .8s ease-in-out infinite; }
        .mri-coin-3 { animation: mri-coin 3.2s 1.6s ease-in-out infinite; }
        .mri-glow  { animation: mri-glow 3s ease-in-out infinite; }
        .mri-scan  { animation: mri-scan 4.5s ease-in-out infinite; }
      }
    `}</style>

    <svg viewBox="0 0 500 500" className="w-full max-w-[520px] h-auto drop-shadow-2xl" aria-hidden="true">
      <defs>
        <linearGradient id="ringG" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0AC5A8" />
          <stop offset="100%" stopColor="#60B74F" />
        </linearGradient>
        <linearGradient id="ringO" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#00B4E2" />
          <stop offset="100%" stopColor="#7B6BFF" />
        </linearGradient>
        <linearGradient id="ringP" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#FFB65A" />
          <stop offset="100%" stopColor="#FF7ABE" />
        </linearGradient>
        <linearGradient id="barG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0AC5A8" />
          <stop offset="100%" stopColor="#0AC5A8" stopOpacity="0.35" />
        </linearGradient>
        <radialGradient id="glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#7B6BFF" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#7B6BFF" stopOpacity="0" />
        </radialGradient>
        <clipPath id="scanClip">
          <circle cx="250" cy="250" r="120" />
        </clipPath>
      </defs>

      {/* Soft central glow */}
      <circle className="mri-glow" cx="250" cy="250" r="180" fill="url(#glow)" />

      {/* Concentric health rings (Apple-Watch style) */}
      <g fill="none" strokeLinecap="round">
        <circle cx="250" cy="250" r="90"  stroke="#ffffff" strokeOpacity="0.08" strokeWidth="14" />
        <circle cx="250" cy="250" r="70"  stroke="#ffffff" strokeOpacity="0.08" strokeWidth="14" />
        <circle cx="250" cy="250" r="50"  stroke="#ffffff" strokeOpacity="0.08" strokeWidth="14" />
        <circle className="mri-ring-1" cx="250" cy="250" r="90" stroke="url(#ringG)" strokeWidth="14"
                strokeDasharray="565" strokeDashoffset="565" transform="rotate(-90 250 250)" />
        <circle className="mri-ring-2" cx="250" cy="250" r="70" stroke="url(#ringO)" strokeWidth="14"
                strokeDasharray="440" strokeDashoffset="440" transform="rotate(-90 250 250)" />
        <circle className="mri-ring-3" cx="250" cy="250" r="50" stroke="url(#ringP)" strokeWidth="14"
                strokeDasharray="314" strokeDashoffset="314" transform="rotate(-90 250 250)" />
      </g>

      {/* Central "diaphragm" with rupee — pulsing */}
      <g className="mri-heart">
        <circle cx="250" cy="250" r="34" fill="#ffffff" fillOpacity="0.14" />
        <circle cx="250" cy="250" r="24" fill="#ffffff" fillOpacity="0.95" />
        <text x="250" y="262" textAnchor="middle" fontFamily="Georgia, 'Times New Roman', serif"
              fontSize="30" fontWeight="700" fill="#1F135A">₹</text>
      </g>

      {/* Scanning bar (subtle "MRI scan" motion) */}
      <g clipPath="url(#scanClip)">
        <rect className="mri-scan" x="130" y="248" width="240" height="4" fill="#0AC5A8" rx="2" />
      </g>

      {/* ECG heartbeat line running across the top of the rings */}
      <g transform="translate(0,120)">
        <path className="mri-ecg" d="M 20 40 L 110 40 L 130 20 L 150 60 L 175 -10 L 200 70 L 225 40 L 480 40"
              fill="none" stroke="#0AC5A8" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </g>

      {/* Rising bar chart at the bottom — the "MRI" morphs into growth */}
      <g transform="translate(90,380)">
        <line x1="0" y1="80" x2="320" y2="80" stroke="#ffffff" strokeOpacity="0.2" strokeWidth="1" />
        <rect className="mri-bar mri-bar-1" x="0"   y="30" width="42" height="50" rx="6" fill="url(#barG)" />
        <rect className="mri-bar mri-bar-2" x="60"  y="18" width="42" height="62" rx="6" fill="url(#barG)" />
        <rect className="mri-bar mri-bar-3" x="120" y="34" width="42" height="46" rx="6" fill="url(#barG)" />
        <rect className="mri-bar mri-bar-4" x="180" y="8"  width="42" height="72" rx="6" fill="url(#barG)" />
        <rect className="mri-bar mri-bar-5" x="240" y="0"  width="42" height="80" rx="6" fill="#0AC5A8" />
      </g>

      {/* Floating rupee coins */}
      <g fontFamily="Georgia, 'Times New Roman', serif" fontSize="18" fontWeight="700">
        <text className="mri-coin-1" x="140" y="140" fill="#FFD86B">₹</text>
        <text className="mri-coin-2" x="360" y="150" fill="#0AC5A8">₹</text>
        <text className="mri-coin-3" x="410" y="290" fill="#FFB65A">₹</text>
      </g>

      {/* Stethoscope-esque earpieces at the top corners */}
      <g fill="none" stroke="#ffffff" strokeOpacity="0.35" strokeWidth="2">
        <path d="M 90 90 Q 120 60 250 60 Q 380 60 410 90" />
        <circle cx="90"  cy="90" r="5" fill="#ffffff" fillOpacity="0.35" />
        <circle cx="410" cy="90" r="5" fill="#ffffff" fillOpacity="0.35" />
      </g>
    </svg>

    {/* Trust badges underneath */}
    <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-6 text-white/60 text-[10px] font-semibold tracking-widest uppercase pointer-events-none">
      <span>Diagnose</span>
      <span className="text-brand-green">·</span>
      <span>Prescribe</span>
      <span className="text-brand-green">·</span>
      <span>Grow</span>
    </div>
  </div>
);

export default FinancialMRIIllustration;
